import { useGetV1Search, type EnvelopeSearchResultsDataItemsItem } from "@solarkey/api-client";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useCapabilities } from "../session/capabilities";

type SearchItem = EnvelopeSearchResultsDataItemsItem;

/** Section headers per the wireframe (UX-004 §4). Kinds not listed fall under "Other". */
const GROUP_ORDER: { heading: string; kinds: string[] }[] = [
  { heading: "Sites", kinds: ["site"] },
  { heading: "Pages", kinds: ["page"] },
  { heading: "Equipment", kinds: ["equipment"] },
  { heading: "Actions", kinds: ["command", "work_order"] },
  { heading: "Documents", kinds: ["document"] },
  { heading: "People", kinds: ["person"] },
];

function groupItems(items: SearchItem[]): { heading: string; items: SearchItem[] }[] {
  const groups: { heading: string; items: SearchItem[] }[] = [];
  const seen = new Set<string>();
  for (const g of GROUP_ORDER) {
    const matched = items.filter((it) => it.kind && g.kinds.includes(it.kind));
    matched.forEach((it) => seen.add(`${it.kind}-${it.id}`));
    if (matched.length > 0) groups.push({ heading: g.heading, items: matched });
  }
  const rest = items.filter((it) => !seen.has(`${it.kind}-${it.id}`));
  if (rest.length > 0) groups.push({ heading: "Other", items: rest });
  return groups;
}

/** The site's navigable pages — searched client-side so "Pages at site X"
 *  appears alongside the backend's site/equipment hits (UX-004 §4). The search
 *  index holds sites and equipment only, not navigation targets or actions. */
const SITE_PAGES: { label: string; segment: string }[] = [
  { label: "Now", segment: "now" },
  { label: "Performance", segment: "performance" },
  { label: "Blocks", segment: "blocks" },
  { label: "Meters and sensors", segment: "sensors" },
  { label: "Alerts", segment: "alerts" },
  { label: "Equipment", segment: "equipment" },
  { label: "Strings and combiners", segment: "strings" },
  { label: "Schematic", segment: "schematic" },
  { label: "Weather and forecast", segment: "weather" },
  { label: "Data path and coverage", segment: "datapath" },
  { label: "Settings", segment: "settings" },
];

/** Synthesise the Pages-at-site and Actions results the backend does not index. */
function contextualItems(q: string, tenant: string, scope: string, site: string | undefined): SearchItem[] {
  if (!site) return [];
  const needle = q.trim().toLowerCase();
  const base = `/t/${tenant}/s/${scope}/plants/${site}`;
  const pages: SearchItem[] = SITE_PAGES
    .filter((p) => p.label.toLowerCase().includes(needle))
    .map((p) => ({ kind: "page", id: `${site}-${p.segment}`, label: p.label, scope: site, route: `${base}/${p.segment}` }));

  const actions: SearchItem[] = [];
  if ("acknowledge alerts".includes(needle)) {
    actions.push({ kind: "command", id: `${site}-ack`, label: "Acknowledge alerts", scope: site, route: `${base}/alerts` });
  }
  return [...pages, ...actions];
}

/**
 * CommandPalette (UX-004 Shell) — ⌘K / Ctrl+K opens a search overlay backed by
 * GET /v1/search (live). Site results navigate into the scoped route tree; other
 * kinds are listed. The single soft shadow (UX-004 §2.3) is the only elevation.
 * Escape closes; the input takes focus on open; results are keyboard reachable.
 */
export function CommandPalette(): React.ReactNode {
  const caps = useCapabilities();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const m = pathname.match(/^\/t\/([^/]+)\/s\/([^/]+)/);
  const tenant = m?.[1] || caps.scopes[0]?.id || "";
  const scope = m?.[2] || caps.scopes[0]?.id || "";
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  function routeFor(it: SearchItem): string | undefined {
    const r = it.route;
    if (r) {
      // Synthetic items already carry a SPA route.
      if (r.startsWith("/t/")) return r;
      // Backend emits scope-agnostic /sites/{slug}/{page}; map into the route tree.
      const mSite = r.match(/^\/sites\/([^/]+)(\/.*)?$/);
      if (mSite) {
        const base = `/t/${tenant}/s/${scope}/plants/${mSite[1]}${mSite[2] ?? "/now"}`;
        // Equipment hits open the P7 page focused on that equipment.
        if (it.kind === "equipment" && it.id && mSite[2]?.startsWith("/equipment")) {
          return `${base}?equipment=${encodeURIComponent(it.id)}`;
        }
        return base;
      }
      // Any other backend route shape has no SPA home yet.
      return undefined;
    }
    if (it.kind === "site" && it.id) {
      return `/t/${tenant}/s/${scope}/plants/${it.id}/now`;
    }
    return undefined;
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    else setQ("");
  }, [open]);

  const query = useGetV1Search({ q }, { query: { enabled: open && q.trim().length >= 2 } });
  const apiItems = query.data?.status === 200 ? (query.data.data.data.items ?? []) : [];

  // Pages and actions aren't in the search index — synthesise them for the site in
  // context (the current route's site, else the first site the backend matched).
  const routeSite = pathname.match(/\/plants\/([^/]+)/)?.[1];
  const contextSite = routeSite ?? apiItems.find((it) => it.kind === "site")?.id ?? undefined;
  const items = [...apiItems, ...contextualItems(q, tenant, scope, contextSite)];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-keyshortcuts="Meta+K Control+K"
        aria-label="Search sites, equipment, work orders, people"
        style={triggerStyle}
      >
        <Search aria-hidden size={13} style={{ flexShrink: 0, opacity: 0.5 }} />
        <span style={{ flex: 1, textAlign: "left" }}>Search sites, equipment, work orders, people</span>
        <kbd style={kbdStyle}>⌘K</kbd>
      </button>

      {open ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh]">
          <button
            type="button"
            aria-label="Close search"
            tabIndex={-1}
            className="absolute inset-0 bg-black/30"
            onClick={() => setOpen(false)}
          />
          <div
            role="dialog"
            aria-label="Search"
            aria-modal="true"
            className="relative w-full max-w-xl rounded border border-border bg-surface"
            style={{ boxShadow: "var(--sk-shadow-float)" }}
          >
            <div className="flex items-center gap-2 border-b border-border px-3 py-2">
              <Search aria-hidden size={16} className="text-text-muted" />
              <input
                ref={inputRef}
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search sites, equipment…"
                aria-label="Search sites and equipment"
                className="w-full bg-transparent py-1 outline-none"
              />
              <kbd className="rounded border border-border px-1 text-xs text-text-muted">Esc</kbd>
            </div>
            <div className="max-h-80 overflow-auto p-1" role="listbox" aria-label="Results">
              {q.trim().length < 2 ? (
                <p className="px-3 py-2 text-sm text-text-muted">Type at least two characters.</p>
              ) : query.isLoading ? (
                <p className="px-3 py-2 text-sm text-text-muted">Searching…</p>
              ) : items.length === 0 ? (
                <p className="px-3 py-2 text-sm text-text-muted">No matches.</p>
              ) : (
                groupItems(items).map((group) => {
                  const heading =
                    group.heading === "Pages" && contextSite
                      ? `Pages at site ${contextSite}`
                      : group.heading;
                  return (
                  <section key={group.heading} aria-label={heading}>
                    <h3 className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                      {heading}
                    </h3>
                    <ul>
                      {group.items.map((it, i) => {
                        const route = routeFor(it);
                        const row = (
                          <>
                            <span>{it.label}</span>
                            {it.scope ? (
                              <span className="text-xs text-text-muted">{it.scope}</span>
                            ) : null}
                          </>
                        );
                        return (
                          <li key={`${it.kind}-${it.id}-${i}`} role="option" aria-selected="false">
                            {route ? (
                              <a
                                href={route}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setOpen(false);
                                  const [path, qs] = route.split("?");
                                  const search = qs
                                    ? Object.fromEntries(new URLSearchParams(qs))
                                    : undefined;
                                  void navigate({ to: path as never, search: search as never });
                                }}
                                className="flex items-center justify-between rounded px-3 py-2 hover:bg-surface-raised focus:bg-surface-raised focus:outline-none"
                              >
                                {row}
                              </a>
                            ) : (
                              <div className="flex items-center justify-between px-3 py-2">{row}</div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                  );
                })
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

const triggerStyle: React.CSSProperties = {
  flex: "0 1 360px",
  maxWidth: 360,
  height: 30,
  border: "1px solid var(--sk-color-border-2)",
  borderRadius: "var(--sk-radius-control, 6px)",
  display: "flex",
  alignItems: "center",
  padding: "0 10px",
  gap: 6,
  color: "var(--sk-color-ink-3)",
  background: "var(--sk-color-surface-1)",
  cursor: "pointer",
  fontSize: 11,
  whiteSpace: "nowrap",
  overflow: "hidden",
};

const kbdStyle: React.CSSProperties = {
  fontSize: 10,
  padding: "1px 5px",
  borderRadius: 4,
  border: "1px solid var(--sk-color-border-2)",
  color: "var(--sk-color-ink-3)",
  background: "var(--sk-color-surface-0)",
  flexShrink: 0,
};
